import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { prepareHeaders, buildError, API } from '../../api/apis';
import { ApiResponse, DescribeTenantSettingsRequest, DescribeTenantSettingsResponse, FeatureFlagsTypeEnum, UpdateTenantSettingsRequest, UpdateTenantSettingsResponse } from '../../interfaces/interfaces';

// Define the RTK Query service for settings
export const settingsService = createApi({
  reducerPath: 'settingsApi',
  tagTypes: ['Settings', 'FeatureFlags'],
  baseQuery: fetchBaseQuery({
    prepareHeaders: prepareHeaders,
  }),
  endpoints: (builder) => ({
    getFeatureFlags: builder.query({
      query: (requestSettings: DescribeTenantSettingsRequest) => {
        const result = API.FETCH_SETTINGS(requestSettings);
        return result || ''; // Ensure a valid string is returned
      },
      transformResponse: (response: ApiResponse) => {
        return response.Response?.FeatureFlags as Record<FeatureFlagsTypeEnum, boolean>[] || [];
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
      providesTags: () => [{ type: 'FeatureFlags', id: 'DESCRIBE' }],
    }),
    getTenantSettings: builder.query({
      query: (requestSettings: DescribeTenantSettingsRequest | undefined) => {
        const result = API.FETCH_SETTINGS(requestSettings);
        return result || ''; // Ensure a valid string is returned
      },
      providesTags: () => [{ type: 'Settings', id: 'DESCRIBE' }],
      transformResponse: (response: ApiResponse) => {
        return response.Response as DescribeTenantSettingsResponse;
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
    }),
    updateTenantSettings: builder.mutation({
      query: (requestUpdateSettings: UpdateTenantSettingsRequest) => ({
        url: API.UPDATE_SETTINGS(requestUpdateSettings),
        method: 'PUT',
        body: requestUpdateSettings,
      }),
      invalidatesTags: [{ type: 'Settings', id: 'DESCRIBE' }],
      transformResponse: (response: ApiResponse) => {
        return response.Response as UpdateTenantSettingsResponse;
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
    }),
  }),
});

// Export hooks for usage in functional components
export const { useGetFeatureFlagsQuery, useGetTenantSettingsQuery, useUpdateTenantSettingsMutation } = settingsService;
